import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatEuro, formatDate, todayISO, relativeDays } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from "recharts";
import { ArrowLeft, Trash2, Pencil, TrendingUp, Wallet, Coins, Calendar, Receipt, AlertCircle, Merge, Link2Off } from "lucide-react";
import DebtForm from "../components/DebtForm";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";

export default function DebtDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [debt, setDebt] = useState(null);
  const [payments, setPayments] = useState([]);
  const [interests, setInterests] = useState([]);
  const [mode, setMode] = useState("simple");
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  // Payment form state
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(todayISO());
  const [payNotes, setPayNotes] = useState("");

  // Interest / Extra-charge form state
  const [intAmount, setIntAmount] = useState("");
  const [intDate, setIntDate] = useState(todayISO());
  const [intNotes, setIntNotes] = useState("");
  const [intKind, setIntKind] = useState("interest");

  const [activity, setActivity] = useState([]);
  const [relatedDebts, setRelatedDebts] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, p, i, a, all] = await Promise.all([
        api.get(`/debts/${id}`),
        api.get(`/debts/${id}/payments`),
        api.get(`/debts/${id}/interest`),
        api.get(`/debts/${id}/activity`),
        api.get(`/debts`),
      ]);
      setDebt(d.data.debt);
      setPayments(p.data.payments || []);
      setInterests(i.data.interest_updates || []);
      setActivity(a.data.events || []);
      setMode(d.data.debt.projection_mode || "simple");
      setRelatedDebts(all.data.debts || []);
    } catch (err) {
      toast.error("Could not load debt");
    } finally {
      setLoading(false);
    }
  };

  const loadProjection = async (m) => {
    try {
      const { data } = await api.get(`/debts/${id}/projection`, { params: { mode: m } });
      setSchedule(data.schedule || []);
    } catch {
      setSchedule([]);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [id]);
  useEffect(() => { if (debt) loadProjection(mode); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [mode, debt?.id, debt?.debt_left]);

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/debts/${id}/payments`, {
        amount: Number(payAmount),
        payment_date: payDate,
        notes: payNotes,
      });
      setPayAmount(""); setPayNotes(""); setPayDate(todayISO());
      toast.success("Payment recorded");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add payment");
    }
  };

  const deletePayment = async (pid) => {
    try {
      await api.delete(`/debts/${id}/payments/${pid}`);
      toast.success("Payment deleted");
      await loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addInterest = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/debts/${id}/interest`, {
        amount: Number(intAmount),
        update_date: intDate,
        notes: intNotes,
        kind: intKind,
      });
      setIntAmount(""); setIntNotes(""); setIntDate(todayISO());
      toast.success(intKind === "extra_charge" ? "Extra charge added" : "Interest added");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add entry");
    }
  };

  const deleteInterest = async (iid) => {
    try {
      await api.delete(`/debts/${id}/interest/${iid}`);
      toast.success("Interest removed");
      await loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const deleteDebt = async () => {
    try {
      await api.delete(`/debts/${id}`);
      toast.success("Debt deleted");
      navigate("/debts");
    } catch {
      toast.error("Could not delete");
    }
  };

  const unlinkFromParent = async () => {
    if (!debt?.paid_by_debt_id) return;
    try {
      const parentId = debt.paid_by_debt_id;
      const parent = relatedDebts.find((r) => r.id === parentId);
      const nextList = (parent?.consolidates || []).filter((cid) => cid !== id);
      await api.put(`/debts/${parentId}`, { consolidates: nextList });
      toast.success("Consolidation removed");
      loadAll();
    } catch {
      toast.error("Could not unlink");
    }
  };

  const parentDebt = useMemo(
    () => (debt?.paid_by_debt_id ? relatedDebts.find((r) => r.id === debt.paid_by_debt_id) : null),
    [debt, relatedDebts]
  );
  const childDebts = useMemo(
    () => (debt?.consolidates || []).map((cid) => relatedDebts.find((r) => r.id === cid)).filter(Boolean),
    [debt, relatedDebts]
  );

  const gross = useMemo(() => {
    if (!debt) return 0;
    return (debt.initial_debt || 0) + (debt.extra_charges || 0) + (debt.interest_added || 0);
  }, [debt]);
  const pct = gross > 0 && debt ? Math.min(100, Math.round(((debt.total_paid || 0) / gross) * 100)) : 0;

  if (loading || !debt) {
    return <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs">Loading…</div>;
  }

  return (
    <div className="space-y-8 fade-in">
      <div>
        <Link
          to="/debts"
          className="inline-flex items-center gap-1 text-sm text-[#5C635A] hover:text-[#1D3328]"
          data-testid="back-to-debts"
        >
          <ArrowLeft className="w-4 h-4" /> All debts
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-xs tracking-widest uppercase text-[#8B9389] bg-[#EEEBE2] px-2 py-1 rounded-md">
              {debt.code}
            </span>
            <StatusBadge status={debt.status} size="lg" />
            {debt.needs_refresh && (
              <span
                className="inline-flex items-center gap-1 rounded-md bg-[#FAEEE5] border border-[#E07A5F]/40 text-[#B04A32] text-xs font-medium px-2 py-1"
                title={`No activity for ${debt.days_since_update} days`}
                data-testid="debt-stale-badge"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                Refresh due · {relativeDays(debt.days_since_update)}
              </span>
            )}
            <span className="text-xs text-[#8B9389]">to {debt.who}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A]">
            {debt.owed_to}
          </h1>
          {debt.why && <p className="text-[#5C635A] mt-3 max-w-2xl">{debt.why}</p>}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" className="rounded-xl border-[#E5E3DB]"
            onClick={() => setEditOpen(true)}
            data-testid="debt-edit-button"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="rounded-xl border-[#E07A5F]/30 text-[#B04A32] hover:bg-[#F7E6DF]"
                data-testid="debt-delete-button"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this debt?</AlertDialogTitle>
                <AlertDialogDescription>
                  All payments and interest records for this debt will be removed. This cannot be
                  undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="debt-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={deleteDebt}
                  className="bg-[#E07A5F] hover:bg-[#C8624A]"
                  data-testid="debt-delete-confirm"
                >
                  Yes, delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Consolidation banners */}
      {parentDebt && (
        <section
          className="rounded-2xl border border-[#A9A3C4] bg-[#EDECF4] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
          data-testid="consolidated-by-banner"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/70 text-[#4B4478] flex items-center justify-center shrink-0">
              <Merge className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <p className="label-kicker text-[#4B4478]">Consolidated debt</p>
              <p className="text-sm text-[#2A2545] mt-0.5">
                This debt was paid off by{" "}
                <Link
                  to={`/debts/${parentDebt.id}`}
                  className="font-medium text-[#4B4478] hover:text-[#2A2545] underline underline-offset-2"
                  data-testid="parent-debt-link"
                >
                  {parentDebt.code} · {parentDebt.owed_to}
                </Link>
                {debt.paid_by_date && (
                  <span className="text-[#5C635A]"> on {formatDate(debt.paid_by_date.slice(0, 10))}</span>
                )}
                . The payment log is frozen for reference.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={unlinkFromParent}
            className="rounded-xl border-[#A9A3C4] hover:bg-white text-[#4B4478] shrink-0"
            data-testid="unlink-consolidation-btn"
          >
            <Link2Off className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
            Unlink
          </Button>
        </section>
      )}
      {childDebts.length > 0 && (
        <section
          className="rounded-2xl border border-[#A9A3C4] bg-[#EDECF4] p-4 sm:p-5"
          data-testid="consolidates-list-banner"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/70 text-[#4B4478] flex items-center justify-center shrink-0">
              <Merge className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div>
              <p className="label-kicker text-[#4B4478]">Consolidator</p>
              <p className="text-sm text-[#2A2545] mt-0.5">
                This debt paid off {childDebts.length} other debt{childDebts.length === 1 ? "" : "s"}.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pl-13">
            {childDebts.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/debts/${c.id}`}
                  className="block rounded-lg bg-white/70 hover:bg-white border border-[#D9D5E6] px-3 py-2 transition-colors"
                  data-testid={`consolidated-child-${c.code}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] tracking-widest uppercase text-[#5C635A]">
                      {c.code}
                    </span>
                    <span className="text-sm text-[#1D3328] font-medium truncate">{c.owed_to}</span>
                  </div>
                  <div className="text-xs text-[#5C635A] mt-0.5">
                    {c.who} · was {formatEuro((c.initial_debt || 0) + (c.extra_charges || 0))}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Top summary cards */}
      <section className="grid grid-cols-2 lg:grid-cols-6 gap-4 stagger">
        <SummaryTile label="Initial" value={formatEuro(debt.initial_debt)} />
        <SummaryTile
          label="Extra charges"
          value={formatEuro((debt.extra_charges || 0) + (debt.extra_charges_added || 0))}
          accent="#B9762E"
        />
        <SummaryTile label="Interest added" value={formatEuro(debt.interest_added)} accent="#C89B6A" />
        <SummaryTile label="Pre-tracking paid" value={formatEuro(debt.opening_paid || 0)} />
        <SummaryTile label="Paid total" value={formatEuro(debt.total_paid)} accent="#2C4A3B" />
        <SummaryTile label="Left" value={formatEuro(debt.debt_left)} accent="#E07A5F" bold />
      </section>

      <section className="card-soft p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="label-kicker">Progress</p>
            <div className="font-display text-xl text-[#1D3328] mt-1">
              {pct}% retired of {formatEuro(gross)}
            </div>
          </div>
          <div className="text-sm text-[#5C635A]">
            <span className="num font-medium text-[#1D3328]">
              {formatEuro(debt.effective_installment ?? debt.monthly_installment)}
            </span>/mo
            {(debt.effective_interest_rate ?? debt.monthly_interest_rate) > 0 && (
              <>
                {" · "}Rate <span className="num font-medium text-[#1D3328]">
                  {((debt.effective_interest_rate ?? debt.monthly_interest_rate) * 100).toFixed(2)}%
                </span>/mo
              </>
            )}
          </div>
        </div>
        <Progress value={pct} className="h-2 bg-[#EEEBE2]" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 text-sm">
          <MiniMeta icon={<Calendar className="w-4 h-4" />} label="Last payment" value={formatDate(debt.last_payment_date)} />
          <MiniMeta
            icon={<Calendar className="w-4 h-4" />}
            label="Next payment"
            value={formatDate(debt.planned_next_payment_date || debt.next_payment_date)}
          />
          <MiniMeta icon={<TrendingUp className="w-4 h-4" />} label="Projected payoff" value={debt.debt_left <= 0 ? "Paid off" : formatDate(debt.projected_payoff_date)} />
        </div>
      </section>

      {/* Projection */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="label-kicker">Projection</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1">Path to zero</h3>
          </div>
          <Tabs value={mode} onValueChange={setMode} className="w-fit">
            <TabsList className="bg-[#EEEBE2]" data-testid="projection-tabs">
              <TabsTrigger value="simple" className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]" data-testid="projection-simple">
                Simple
              </TabsTrigger>
              <TabsTrigger value="amortized" className="data-[state=active]:bg-white data-[state=active]:text-[#1D3328]" data-testid="projection-amortized">
                Amortized
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {schedule.length === 0 ? (
          <p className="text-[#8B9389] text-sm">
            Add a monthly installment {mode === "amortized" ? "and interest rate " : ""}to see a projection.
          </p>
        ) : (
          <>
            <div className="h-64 min-h-[16rem] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={schedule} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="bal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2C4A3B" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2C4A3B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEEBE2" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8B9389" }} interval={Math.max(0, Math.floor(schedule.length / 8))} />
                  <YAxis tick={{ fontSize: 11, fill: "#8B9389" }} tickFormatter={(v) => `€${Math.round(v)}`} />
                  <Tooltip
                    formatter={(v) => formatEuro(v)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <Area type="monotone" dataKey="balance" stroke="#2C4A3B" strokeWidth={2} fill="url(#bal)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 text-sm">
              <Metric label="Months to zero" value={schedule.length} />
              <Metric label="Total interest" value={formatEuro(schedule.reduce((s, r) => s + r.interest, 0))} />
              <Metric label="Total to pay" value={formatEuro(schedule.reduce((s, r) => s + r.payment, 0))} />
              <Metric label="Finishes" value={schedule[schedule.length - 1]?.month} />
            </div>
          </>
        )}
      </section>

      {/* Payments + Interest */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-soft p-6">
          <p className="label-kicker">Payments</p>
          <h3 className="font-display text-xl text-[#1D3328] mt-1 mb-4 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#2C4A3B]" strokeWidth={1.7} /> Payment history
          </h3>

          <form onSubmit={addPayment} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payment-form">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Amount (€)</Label>
              <Input
                type="number" step="0.01" min="0.01" required
                value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                className="rounded-xl border-[#E5E3DB] num" data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Date</Label>
              <Input
                type="date" required value={payDate} onChange={(e) => setPayDate(e.target.value)}
                className="rounded-xl border-[#E5E3DB]" data-testid="payment-date-input"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
              <Textarea
                rows={2} value={payNotes} onChange={(e) => setPayNotes(e.target.value)}
                placeholder="e.g. Installment 17/19"
                className="rounded-xl border-[#E5E3DB]" data-testid="payment-notes-input"
              />
            </div>
            <Button type="submit" className="sm:col-span-2 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white" data-testid="payment-submit">
              Record payment
            </Button>
          </form>
        </div>

        <div className="card-soft p-6">
          <p className="label-kicker">Monthly update</p>
          <h3 className="font-display text-xl text-[#1D3328] mt-1 mb-3 flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#C89B6A]" strokeWidth={1.7} /> Interest & extra charges
          </h3>

          <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
            <div className="rounded-lg bg-[#FCF6E9] border border-[#E5DCC5] px-3 py-2">
              <p className="label-kicker text-[#8B9389]">Interest</p>
              <p className="num font-medium text-[#C89B6A] mt-0.5" data-testid="interest-total">
                {formatEuro(debt.interest_added || 0)}
              </p>
            </div>
            <div className="rounded-lg bg-[#FAEEE5] border border-[#E07A5F]/30 px-3 py-2">
              <p className="label-kicker text-[#8B9389]">Extra charges</p>
              <p className="num font-medium text-[#B9762E] mt-0.5" data-testid="extra-total">
                {formatEuro(debt.extra_charges_added || 0)}
              </p>
            </div>
          </div>

          <form onSubmit={addInterest} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="interest-form">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Type</Label>
              <Tabs value={intKind} onValueChange={setIntKind}>
                <TabsList className="bg-[#EEEBE2] w-full" data-testid="addition-kind-tabs">
                  <TabsTrigger
                    value="interest"
                    className="flex-1 data-[state=active]:bg-white data-[state=active]:text-[#C89B6A]"
                    data-testid="addition-kind-interest"
                  >
                    Interest
                  </TabsTrigger>
                  <TabsTrigger
                    value="extra_charge"
                    className="flex-1 data-[state=active]:bg-white data-[state=active]:text-[#B9762E]"
                    data-testid="addition-kind-extra"
                  >
                    Extra charge
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Amount (€)</Label>
              <Input
                type="number" step="0.01" min="0.01" required
                value={intAmount} onChange={(e) => setIntAmount(e.target.value)}
                className="rounded-xl border-[#E5E3DB] num" data-testid="interest-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Date</Label>
              <Input
                type="date" required value={intDate} onChange={(e) => setIntDate(e.target.value)}
                className="rounded-xl border-[#E5E3DB]" data-testid="interest-date-input"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
              <Textarea
                rows={2} value={intNotes} onChange={(e) => setIntNotes(e.target.value)}
                placeholder={intKind === "extra_charge" ? "e.g. Late fee · court cost" : "e.g. Monthly interest for Feb"}
                className="rounded-xl border-[#E5E3DB]" data-testid="interest-notes-input"
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              className="sm:col-span-2 rounded-xl border-[#E5E3DB] text-[#1D3328] hover:bg-[#EEEBE2]"
              data-testid="interest-submit"
            >
              {intKind === "extra_charge" ? "Add extra charge" : "Add interest"}
            </Button>
          </form>
        </div>
      </section>

      {/* Unified activity log */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-kicker">All events</p>
            <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#2C4A3B]" strokeWidth={1.7} />
              Activity log
            </h3>
          </div>
          <span className="text-xs text-[#8B9389]">{activity.length} entr{activity.length === 1 ? "y" : "ies"}</span>
        </div>

        {activity.length === 0 ? (
          <p className="text-[#8B9389] text-sm">No payments, interest or extra charges recorded yet.</p>
        ) : (
          <ul className="divide-y divide-[#EEEBE2]" data-testid="debt-activity-log">
            {activity.map((e) => {
              const isPay = e.kind === "payment";
              const isExtra = e.kind === "extra_charge";
              const color = isPay ? "#2C4A3B" : isExtra ? "#B9762E" : "#C89B6A";
              const bg = isPay ? "#E5EFE7" : isExtra ? "#FAEEE5" : "#FCF6E9";
              const border = isPay ? "#829A88" : isExtra ? "#E07A5F" : "#E5DCC5";
              const label = isPay ? "Payment" : isExtra ? "Extra charge" : "Interest";
              const sign = e.sign < 0 ? "−" : "+";
              return (
                <li
                  key={`${e.kind}-${e.id}`}
                  className="py-3 flex items-start justify-between gap-3 flex-wrap"
                  data-testid="debt-activity-row"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[10px] tracking-widest uppercase font-bold rounded-md px-2 py-0.5 border"
                        style={{ background: bg, borderColor: border, color }}
                        data-testid="debt-activity-kind"
                      >
                        {label}
                      </span>
                      <span className="text-xs text-[#8B9389]">{formatDate(e.date)}</span>
                    </div>
                    {e.notes && (
                      <p className="text-xs text-[#5C635A] mt-1 whitespace-pre-wrap">{e.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="num font-medium" style={{ color }}>
                      {sign} {formatEuro(e.amount)}
                    </span>
                    <button
                      onClick={() => isPay ? deletePayment(e.id) : deleteInterest(e.id)}
                      className="text-xs text-[#B04A32] hover:text-[#8A3823]"
                      data-testid="debt-activity-delete"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {debt.notes && (
        <section className="card-soft p-6">
          <p className="label-kicker">Notes on this debt</p>
          <p className="text-[#1D3328] mt-2 whitespace-pre-wrap">{debt.notes}</p>
        </section>
      )}

      <DebtForm open={editOpen} onOpenChange={setEditOpen} onSaved={() => loadAll()} debt={debt} />
    </div>
  );
}

function SummaryTile({ label, value, accent = "#1D3328", bold = false }) {
  return (
    <div className="card-soft p-4">
      <p className="label-kicker">{label}</p>
      <div
        className={`num mt-1 ${bold ? "font-display text-2xl font-medium" : "text-lg font-medium"}`}
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function MiniMeta({ icon, label, value, highlight = false }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          highlight
            ? "bg-[#E5EFE7] text-[#2C4A3B] ring-2 ring-[#829A88]/40"
            : "bg-[#EEEBE2] text-[#2C4A3B]"
        }`}
      >
        {icon}
      </div>
      <div>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#8B9389]">{label}</p>
        <p className="text-sm font-medium text-[#1D3328]">{value}</p>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#8B9389]">{label}</span>
      <span className="text-sm font-medium text-[#1D3328] num">{value}</span>
    </div>
  );
}
